import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  Container,
} from "@mui/material";
import contactImage from "../../assets/images/contact_img.png";
import LabeledInput from "../../components/LabeledInput ";
import { CustomButton, HeadingText } from "../../utils/CustomTagComponents";
import CustomSelect from "../../components/CustomSelect ";
import { submitContactForm } from "../../services/ContactFormService";
import { useSelector } from "react-redux";
import { useCountry } from "../../context/CountryContext";
import { showToast } from "../../components/common/Toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const allMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ContactFormSection = () => {
  const { country_id, country_name, auth } = useSelector((state) => state.auth);
  const userId = auth?.localId;

  const { countries, hasMore, loading, page, setPage, fetchCountries } =
    useCountry();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    destination: "Germany",
    planMonth: "",
    planYear: "",
  });

  const [errors, setErrors] = useState({});
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const leftVariant = {
    hidden: { opacity: 0, x: -80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 }
    }
  };

  const rightVariant = {
    hidden: { opacity: 0, x: 80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 }
    }
  };

  const containerVariant = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12
      }
    }
  };

  const fieldVariant = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35 }
    }
  };
  // Generate years (current + next 10)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const futureYears = Array.from({ length: 11 }, (_, i) =>
      (currentYear + i).toString()
    );
    setYears(futureYears);
  }, []);

  // Update months dynamically
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonthIndex = now.getMonth();

    if (!formData.planYear) {
      setFormData((prev) => ({
        ...prev,
        planYear: currentYear.toString(),
        planMonth: allMonths[currentMonthIndex],
      }));
      setMonths(allMonths.slice(currentMonthIndex));
    } else if (parseInt(formData.planYear) === currentYear) {
      setMonths(allMonths.slice(currentMonthIndex));
      if (
        !formData.planMonth ||
        allMonths.indexOf(formData.planMonth) < currentMonthIndex
      ) {
        setFormData((prev) => ({
          ...prev,
          planMonth: allMonths[currentMonthIndex],
        }));
      }
    } else {
      setMonths(allMonths);
      if (!formData.planMonth)
        setFormData((prev) => ({ ...prev, planMonth: "January" }));
    }
  }, [formData.planYear]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" })); // clear error on change
  };

  // Validation function
  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First Name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile Number is required";
    if (!formData.destination)
      newErrors.destination = "Destination is required";
    // if (!formData.planMonth) newErrors.planMonth = "Select a month";
    // if (!formData.planYear) newErrors.planYear = "Select a year";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      firstname: formData.firstName,
      lastname: formData.lastName,
      email: formData.email,
      mobile: formData.mobile,
      preferred_destination: formData.destination,
      // preferred_plan: `${formData.planMonth}`, // if plan type needed, replace with another field
      // preferred_year: formData.planYear,
      consent_enable: formData.consent,
      user_id: userId, // static for now — replace with logged-in user
      country_id: country_id, // static for now — replace dynamically if needed
    };

    try {
      const res = await submitContactForm(payload);
      showToast("Form Submitted Successfully!");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        destination: "",
        planMonth: "",
        planYear: "",
        consent: false,
      });
    } catch (err) {
      console.error("Submit failed:", err);
      showToast("Error while submitting form!", "error");
    }
  };

  return (
    <Box component={motion.div}
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      sx={{ overflow:'hidden',bgcolor: "#002045", my: { xs: 2, sm: 2, md: 3 }, }}>
      <Container sx={{
        py: { xs: 3, sm: 4, md: 6 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" }
      }}>
        {/* <Grid container spacing={4} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}></Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <HeadingText variant="h5" sx={{ mb: 5, color: "#ffffff" }}>
              Let Our Team Reach Out To You
            </HeadingText>
          </Grid>
        </Grid> */}

        <Grid container spacing={6} alignItems="flex-start">
          <Grid component={motion.div}
            variants={leftVariant} size={{ xs: 12, md: 5 }}>
            <Box
              component="img"
              src={contactImage}
              alt="Contact Visual"
              sx={{
                width: "100%",
                borderRadius: "16px",
                objectFit: "cover",
                maxHeight: { xs: 300, md: 545 },
              }}
            />
          </Grid>

          <Grid component={motion.div}
            variants={rightVariant} size={{ xs: 12, md: 7 }}>
            <Box
              sx={{
                background: "rgba(255,255,255,0.06)",
                borderRadius: "20px",
                padding: { xs: "24px", md: "40px" },
                backdropFilter: "blur(6px)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <HeadingText variant="h5" sx={{
                mb: 2,
                color: "#ffffff",
                fontWeight: 700,
                 lineHeight: '1.3 !important',
                fontSize: { xs: "22px", sm: "26px", md: "30px" },
                // letterSpacing: "0.89px"
              }}>
                Let Our Team Reach Out To You
              </HeadingText>
              <Grid container spacing={2}>
                {/* First Name */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LabeledInput
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  {errors.firstName && (
                    <Typography color="error" fontSize={14} sx={{ mt: 1, color: "red" }} >
                      {errors.firstName}
                    </Typography>
                  )}
                </Grid>

                {/* Last Name */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LabeledInput
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                  {errors.lastName && (
                    <Typography color="error" fontSize={14} sx={{ mt: 1, color: "red" }}>
                      {errors.lastName}
                    </Typography>
                  )}
                </Grid>

                {/* Email */}
                <Grid size={{ xs: 12, sm: 6 }} >
                  <LabeledInput
                    label="Email Id"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <Typography color="error" fontSize={14} sx={{ mt: 1, color: "red" }}>
                      {errors.email}
                    </Typography>
                  )}
                </Grid>

                {/* Mobile */}
                {/* <Grid size={{ xs: 12, sm: 6 }} mb={2}>
                <LabeledInput
                  label="Mobile Number"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                />
                {errors.mobile && (
                  <Typography color="error" fontSize={12}>
                    {errors.mobile}
                  </Typography>
                )}
              </Grid> */}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <label style={{ fontWeight: 600, marginBottom: 8, display: "block", color: "#ffffff", fontSize: "16px" }}>
                    Mobile Number
                  </label>
                  <PhoneInput
                    country={"in"}                  // default India flag
                    value={formData.mobile}
                    onChange={(value) => handleChange({ target: { name: "mobile", value } })}
                    enableSearch={true}             // search country
                    placeholder="Enter mobile number"
                    specialLabel={""}
                    inputStyle={{
                      width: "100%",
                      height: "43px",
                      borderRadius: "8px",
                    }}
                    sx={{ fontSize: '14px' }}
                    buttonStyle={{
                      borderRadius: "8px 0 0 8px",
                    }}
                    containerStyle={{
                      width: "100%",
                    }}
                  />

                  {errors.mobile && (
                    <Typography color="error" fontSize={14} sx={{ mt: 1, color: "red" }}>
                      {errors.mobile}
                    </Typography>
                  )}
                </Grid>

                {/* Destination */}
                <Grid size={{ xs: 12 }} mb={2} >
                  <CustomSelect
                    label="Your Preferred Destination"
                    options={countries.map((c) => c.name)} // ✅ use shared data
                    value={formData.destination}
                    onChange={(e) =>
                      handleChange({
                        target: { name: "destination", value: e.target.value },
                      })
                    }
                    // name="destination"
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: {
                        onScroll: (event) => {
                          const { scrollTop, scrollHeight, clientHeight } =
                            event.target;
                          if (
                            scrollHeight - scrollTop <= clientHeight + 10 &&
                            hasMore &&
                            !loading
                          ) {
                            const nextPage = page + 1;
                            setPage(nextPage);
                            fetchCountries(nextPage);
                          }
                        },
                        style: { maxHeight: 300, overflowY: "auto" },
                      },
                    }}
                  />
                  {errors.destination && (
                    <Typography color="error" fontSize={14} sx={{ color: 'red' }}>
                      {errors.destination}
                    </Typography>
                  )}
                </Grid>

                {/* Month */}
                {/* <Grid size={{ xs: 12, sm: 6 }} mb={2}>
                <CustomSelect
                  label="When Do You Plan To?"
                  options={months}
                  value={formData.planMonth}
                  onChange={(e) =>
                    handleChange({
                      target: { name: "planMonth", value: e.target.value },
                    })
                  }
                  name="planMonth"
                />
                {errors.planMonth && (
                  <Typography color="error" fontSize={12}>
                    {errors.planMonth}
                  </Typography>
                )}
              </Grid> */}

                {/* Year */}
                {/* <Grid size={{ xs: 12, sm: 6 }} mb={2}>
                <CustomSelect
                  label="Your Preferred Year"
                  options={years}
                  value={formData.planYear}
                  onChange={(e) =>
                    handleChange({
                      target: { name: "planYear", value: e.target.value },
                    })
                  }
                  name="planYear"
                />
                {errors.planYear && (
                  <Typography color="error" fontSize={12}>
                    {errors.planYear}
                  </Typography>
                )}
              </Grid> */}
              </Grid>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.consent || false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        consent: e.target.checked,
                      }))
                    }
                    sx={{
                      marginRight: '10px',
                      marginLeft: '10px',
                      padding: '0px',
                      color: "#fd0000",
                      "&.Mui-checked": { color: "#fd0000" },
                      "& .MuiSvgIcon-root": { borderRadius: "4px" },
                    }}
                  />
                }
                sx={{ mt: 1, alignItems: 'flex-start' }}
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: 11, sm: 16 },
                      fontWeight: 700,
                      color: "#ffffff",
                      lineHeight: { xs: "15px", sm: "20px" },
                      // letterSpacing: "0.89px",
                    }}
                  >
                    I consent to receiving Calls, WhatsApp, Email and Google RCS
                    from glapsol to assist with this enquiry.
                  </Typography>
                }
              />

              <Box mt={2}>
                <CustomButton variant="contained" onClick={handleSubmit}>
                  Submit
                </CustomButton>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default ContactFormSection;
